/**
 * Query key factory for Bookmarks
 * 
 * This file provides a consistent way to define query keys for React Query
 * to properly deduplicate and invalidate related queries.
 */

/**
 * Filters for bookmark queries
 */
export interface BookmarkFilters {
  searchTerm?: string;
  sortBy?: 'created_at' | 'title' | 'url';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Centralized query keys for bookmarks to ensure consistent cache management
 */
export const bookmarkKeys = {
  // Base key for all bookmark-related queries
  all: ['bookmarks'] as const,
  
  // Key for lists of bookmarks
  lists: () => [...bookmarkKeys.all, 'list'] as const,
  
  // Key for filtered lists with parameters
  list: (filters: BookmarkFilters) => [...bookmarkKeys.lists(), filters] as const,
  
  // Key for bookmarks by topic ID
  byTopic: (topicId: string) => [...bookmarkKeys.lists(), { topicId }] as const,
  
  // Key for filtered bookmarks by topic ID
  byTopicWithFilters: (topicId: string, filters: BookmarkFilters) => 
    [...bookmarkKeys.byTopic(topicId), filters] as const,
  
  // Base key for bookmark detail queries
  details: () => [...bookmarkKeys.all, 'detail'] as const,
  
  // Key for a specific bookmark by ID
  detail: (id: string) => [...bookmarkKeys.details(), id] as const,
  
  // Key for storing filter settings for a specific topic
  filters: (topicId: string) => [...bookmarkKeys.all, 'filters', topicId] as const,
};